/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Config } from '../../config/config';
import config from '../../config/config.json';
import { useGlobalStore } from '../../stores/global/global.store';
import { useUserProfileStore } from '../../stores/user-profile/user-profile.store';
import { Navigation } from '../../ui-library/Patterns/Navigation';
import { buttonClickEvent } from '../../utils/analytics/event';
import { getAuthToken } from '../../utils/auth';
import { ProviderSelector } from '../Stats/ProviderSelector';
import { Tab } from '../Tab';

import BarChart from '@icons/BarChart.svg';
import HistoryOutline from '@icons/HistoryOutline.svg';
import QuestionOutline from '@icons/QuestionOutline.svg';

export const AppNavigation = () => {
  const { t: tCommon } = useTranslation('common', { keyPrefix: 'common' });
  const { asPath } = useRouter();
  const [providersEnabled, setProvidersEnabled] = useState<boolean | null>(null);
  const { selectedProvider, setSelectedProvider } = useGlobalStore();
  const { accountProviders, accountStatus, defaultProvider } = useUserProfileStore();
  const authToken = getAuthToken();

  const { globalContent } = config as Config;

  const navigationTabs = [
    { id: 'statistics', title: tCommon('statistics'), url: '/', icon: BarChart },
    {
      id: 'recent-match-history',
      title: tCommon('recent-match-history'),
      url: '/match-history/',
      icon: HistoryOutline,
    },
    { id: 'faq', title: tCommon('faq-short'), url: '/faq/', icon: QuestionOutline },
  ];
  const currentTab = navigationTabs.find((tab) => tab.url === asPath);
  const currentPageId = currentTab ? currentTab.id : 'statistics';

  useEffect(() => {
    if (!selectedProvider && defaultProvider) {
      setSelectedProvider(defaultProvider);
    }
  }, [defaultProvider]);

  useEffect(() => {
    const hasProviders =
      accountProviders.length > 0 && accountStatus !== 'no-game-accounts' && accountStatus !== 'no-game-ownership';

    if (!hasProviders || !defaultProvider) {
      setProvidersEnabled(hasProviders);
      return;
    }

    const providerExists = accountProviders.some((provider) => provider.provider === selectedProvider);
    const shouldUseDefault = accountStatus === 'synced' && selectedProvider !== defaultProvider;

    if (!providerExists || shouldUseDefault) {
      setSelectedProvider(defaultProvider);
    }

    setProvidersEnabled(hasProviders);
  }, [accountProviders]);

  return (
    <>
      <header
        className={classNames(
          'sticky top-0 z-10',
          'h-18 w-full',
          'inline-flex',
          'p-4 lg:px-10 lg:py-0 2xl:px-24 4xl:px-20',
          'bg-surface-black/30 backdrop-blur-md',
          'border-b border-white/12',
          'group-data-bottom/scroll-wrapper:translate-y-0',
          'group-data-scroll-up/scroll-wrapper:translate-y-0',
          'group-data-scroll-down/scroll-wrapper:-translate-y-18',
          'group-data-scroll-down/scroll-wrapper:lg:translate-y-0',
          'transition-transform duration-300 ease-in-out',
        )}
      >
        <div className='mx-auto flex w-full max-w-nav flex-1 items-center justify-between gap-x-10'>
          <Link href={'/'} scroll={false} className='h-full lg:h-auto lg:w-full lg:max-w-90'>
            <img className='hidden lg:flex' src={globalContent.logoAlt} alt={globalContent.title} />
            <img className='max-h-full lg:hidden' src={globalContent.logo} alt={globalContent.title} />
          </Link>
          {authToken && (
            <div className='flex h-full w-0 flex-1 justify-end lg:w-auto lg:justify-between lg:gap-x-10'>
              <Navigation className='hidden shrink-0 lg:inline-flex' direction='row' label='primary-navigation'>
                {navigationTabs.map(({ id, title, url }) => (
                  <Tab
                    key={id}
                    href={url}
                    active={url === asPath}
                    onClick={() => {
                      buttonClickEvent(id, currentPageId);
                    }}
                  >
                    {title}
                  </Tab>
                ))}
              </Navigation>
              <div className='max-w-fit min-w-0 flex-1 lg:py-4'>
                <ProviderSelector
                  providers={accountProviders}
                  accountStatus={accountStatus}
                  providersEnabled={providersEnabled}
                />
              </div>
            </div>
          )}
        </div>
      </header>
      {authToken && (
        <Navigation
          className={classNames(
            'fixed bottom-0 z-20',
            'inline-flex shrink-0 justify-evenly lg:hidden',
            'h-14 w-full',
            'bg-surface-black/30 backdrop-blur-md',
            'border-t border-white/12',
            'px-4',
            'group-data-bottom/scroll-wrapper:translate-y-0',
            'group-data-scroll-up/scroll-wrapper:translate-y-0',
            'group-data-scroll-down/scroll-wrapper:translate-y-18',
            'group-data-scroll-down/scroll-wrapper:lg:translate-y-0',
            'transition-transform duration-300 ease-in-out',
          )}
          direction='row'
          label='primary-navigation'
        >
          {navigationTabs.map(({ id, title, url, icon }) => (
            <Tab
              key={id}
              href={url}
              icon={icon}
              active={url === asPath}
              onClick={() => {
                buttonClickEvent(id, currentPageId);
              }}
            >
              {title}
            </Tab>
          ))}
        </Navigation>
      )}
    </>
  );
};
